import type { Coding, Resource } from "@medplum/fhirtypes";
import { codeSystemUrl, extensionUrl } from "@repo/fhir-systems";
import { FHIR_CANONICAL_BASE } from "./constants";

export const DIAGNOSTIC_RESOURCE_KIND_SYSTEM = codeSystemUrl("diagnostic-resource-kind");
export const DIAGNOSTIC_PROCEDURE_KIND = "procedure";
export const DIAGNOSTIC_OBSERVATION_KIND = "observation";
export const DIAGNOSTIC_UNCAPTURED_KIND = "uncaptured-observation";
export const DIAGNOSTIC_UNCAPTURED_ID_SYSTEM = `${FHIR_CANONICAL_BASE}/identifier/diagnostic-uncaptured-observation`;
export const DIAGNOSTIC_OBSERVATION_DEFINITION_KIND = "observation-definition";

export function diagnosticResourceKindToken(kind: string): string {
  return `${DIAGNOSTIC_RESOURCE_KIND_SYSTEM}|${kind}`;
}

export function diagnosticResourceKindTag(kind: string): Coding {
  return { system: DIAGNOSTIC_RESOURCE_KIND_SYSTEM, code: kind };
}

export function hasDiagnosticResourceKind(resource: Resource, kind: string): boolean {
  return (
    resource.meta?.tag?.some((tag) => tag.system === DIAGNOSTIC_RESOURCE_KIND_SYSTEM && tag.code === kind) ?? false
  );
}
export const DIAGNOSTIC_DEFINITION_CODE_SYSTEM = codeSystemUrl("diagnostic-definition");

export const DIAGNOSTIC_ADMINISTRATION_PARAMETER_URL = extensionUrl("diagnostic-administration-parameter");
export const DIAGNOSTIC_ADMINISTRATION_VALUE_URL = extensionUrl("diagnostic-administration-value");
export const DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL = extensionUrl("diagnostic-execution-declaration-snapshot");
export const DIAGNOSTIC_PROCEDURE_NOTE_LABEL_URL = extensionUrl("diagnostic-procedure-note-label");
export const DIAGNOSTIC_VALUE_DEFINITION_URL = extensionUrl("diagnostic-value-definition");
export const DIAGNOSTIC_MEASUREMENT_CONTEXT_URL = extensionUrl("diagnostic-measurement-context");
export const DIAGNOSTIC_MEASUREMENT_CONTEXT_VALUE_URL = extensionUrl("diagnostic-measurement-context-value");
export const DIAGNOSTIC_COMPONENT_DEFINITION_URL = extensionUrl("diagnostic-component-definition");
export const DIAGNOSTIC_OBSERVATION_KIND_URL = extensionUrl("diagnostic-observation-kind");
export const DIAGNOSTIC_OBSERVATION_NOTE_LABEL_URL = extensionUrl("diagnostic-observation-note-label");
export const DIAGNOSTIC_OCCURRENCE_URL = extensionUrl("diagnostic-observation-occurrence");
export const DIAGNOSTIC_REFERENCE_METADATA_URL = extensionUrl("diagnostic-reference-metadata");
/**
 * Sits ON the `derivedFrom` entry that names the document, because it
 * qualifies that link rather than the measurement: the reading still cites the
 * file, and this says whether the value is still the file's.
 *
 * Written only for a DEPARTURE (`hand`), so a bare link reads as the document's
 * own value. Our entry is rewritten wholesale on every encode, so a re-import
 * that puts the file's number back clears it by not writing it.
 */
export const DIAGNOSTIC_OBSERVATION_VALUE_SOURCE_URL = extensionUrl("diagnostic-observation-value-source");
export const DIAGNOSTIC_OBSERVATION_VALUE_SOURCE_CODES = ["document", "hand"] as const;

export const DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM = codeSystemUrl("diagnostic-observation-definition");
export const DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM = codeSystemUrl("diagnostic-observation-component");
export const DIAGNOSTIC_OBSERVATION_ID_SYSTEM = `${FHIR_CANONICAL_BASE}/identifier/diagnostic-observation-id`;
export const DIAGNOSTIC_CHOICE_CODE_SYSTEM = codeSystemUrl("diagnostic-choice");

export function diagnosticDefinitionCanonical(definitionId: string): string {
  return `${FHIR_CANONICAL_BASE}/ActivityDefinition/${encodeURIComponent(definitionId)}`;
}

export const DIAGNOSTIC_TRIAL_KIND = "diagnostic-trial";
export const DIAGNOSTIC_TRIAL_SOURCE_URL = extensionUrl("diagnostic-trial-source");
export const DIAGNOSTIC_TRIAL_PARAMETER_SYSTEM = codeSystemUrl("diagnostic-trial-parameter");
